import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Row, Col, Toast, ToastContainer } from 'react-bootstrap';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/material_green.css';
import { X, Plus } from 'react-feather';
import '../../../styles/user.css';
import { BulkMedicationCreate, BulkMedicationDelete } from '../../../apiServices/reducer/dialogApiService';
import { useDispatch, useSelector } from 'react-redux';
import CustomSuccessModal from '../../../components/CustomSuccessModal';
import { showPmrDeleteConfirm } from '../../../utils/swalUtils';

const MedicationsModal = ({ show, onHide, medications, onSave, mode = 'add', userId }) => {
  console.log(medications, "medications1")
  const [formData, setFormData] = useState(medications || []);
  const [submitClicked, setSubmitClicked] = useState(false);
  const [maxDate] = useState(new Date());
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const dispatch = useDispatch();
  const inputErrorStyle = { border: '1px solid #dc3545', boxShadow: 'none' };
  const token = useSelector((state) => state.apiService?.token);
  // const userId = useSelector((state) => state.apiService?.login?.user_id); 
  // Add this useEffect to reset submitClicked when modal opens
  useEffect(() => {
    if (show) {
      setSubmitClicked(false);
      setShowSuccessModal(false);
      setSuccessMessage('');

      // Auto scroll to bottom when modal opens to show the plus button
      setTimeout(() => {
        const modalBody = document.querySelector('.modal-body');
        if (modalBody) {
          modalBody.scrollTop = modalBody.scrollHeight;
        }
      }, 200);
    }
  }, [show]);

  const handleInputChange = (index, field, value) => {
    const updatedData = [...formData];
    let newValue = value;

    // For Notes and Status (optional fields), allow empty strings without affecting other data
    if (field === 'MedicationComments' || field === 'Status') {
      updatedData[index] = {
        ...updatedData[index],
        [field]: newValue instanceof Array ? newValue[0] : (newValue || '')
      };
    } else {
      // For required fields, maintain existing behavior
      updatedData[index] = {
        ...updatedData[index],
        [field]: newValue instanceof Array ? newValue[0] : newValue
      };
    }
    setFormData(updatedData);
  };

  useEffect(() => {
    if (show) {
      getMedicationData(medications);
    }
  }, [show, medications]);

  // Handle success modal close
  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    setSuccessMessage('');
  };

  // Handle error modal close
  const handleErrorModalClose = () => {
    // setShowErrorModal(false);
    // setErrorMessage('');
  };

  const getMedicationData = (lstMedications) => {
    if (lstMedications && lstMedications.length > 0) {
      const updated = lstMedications.map((med) => ({
        MedicationName: med.MedicationName || med.medication || '',
        Dose: med.Dose || med.dose || '',
        MedicationRecurrence: med.MedicationRecurrence || med.frequency || '',
        MedicationStartedFrom: med.MedicationStartedFrom || med.startedFrom
          ? new Date(med.MedicationStartedFrom || med.startedFrom)
          : null,
        MedicationComments: med.MedicationComments || med.comments || med.comment || med.notes || '',
        Status: med.Status || med.status || '',
        MedicationID: med.MedicationID || med.medication_id || med.id || med.medicationId || 0
      }));
      setFormData(updated);
    } else {
      setFormData([{
        MedicationName: '',
        Dose: '',
        MedicationRecurrence: '',
        MedicationStartedFrom: null,
        MedicationComments: '',
        Status: '',
        MedicationID: 0
      }]);
    }
  };

  const addItem = () => {
    if (mode === 'edit') return; // Prevent adding in edit mode
    setFormData([
      ...formData,
      {
        MedicationName: '',
        Dose: '',
        MedicationRecurrence: '',
        MedicationStartedFrom: null,
        MedicationComments: '',
        Status: '',
        MedicationID: 0
      }
    ]);

    // Auto scroll to the new row after a short delay to ensure DOM is updated
    setTimeout(() => {
      const modalBody = document.querySelector('.modal-body');
      if (modalBody) {
        modalBody.scrollTop = modalBody.scrollHeight;
      }
    }, 100);
  };

  const deleteItem = async (index) => {
    const med = formData[index];
    showPmrDeleteConfirm({
      message: 'You want to delete this medication? This action cannot be undone.',
    }).then(async (result) => {
      if (result.isConfirmed) {
        // If the medication has a MedicationID (already saved in backend), call delete API
        if (med.MedicationID && med.MedicationID !== 0) {
          try {
            const payload = { UserID: userId, MedicationID: med.MedicationID, token };
            const response = await dispatch(BulkMedicationDelete(payload)).unwrap();
            if (response === true) {
              if (onSave) {
                await onSave(); // Refresh medications in parent
              }
              setSuccessMessage('Medication deleted successfully!');
              setShowSuccessModal(true);
            } else {
            }
          } catch (error) {
          }
        } else {
          // For new (unsaved) rows, just remove from formData
          const updatedData = formData.filter((_, i) => i !== index);
          setFormData(updatedData);
          setSuccessMessage('Medication deleted successfully!');
          setShowSuccessModal(true);
        }
      }
      // If cancelled, do nothing
    });
  };

  // Check if a medication row is completely empty (all fields are empty)
  const isRowEmpty = (med, index) => {
    return !med.MedicationName && 
           !med.Dose && 
           !med.MedicationRecurrence && 
           !med.MedicationStartedFrom && 
           !med.MedicationComments && 
           !med.Status;
  };

  // Check if MedicationName should be required for this row
  // MedicationName is required if:
  // 1. It's the first row (index 0) and it's empty (initial single row)
  // 2. ANY other field (Dose, Frequency, Start Date, Notes, Status) is filled
  const shouldRequireMedicationName = (med, index) => {
    const isCompletelyEmpty = isRowEmpty(med, index);
    const medicationNameEmpty = !med.MedicationName || !med.MedicationName.trim();
    
    // If it's the first row and completely empty, require MedicationName (initial single row validation)
    if (index === 0 && isCompletelyEmpty) {
      return true;
    }
    
    // Check if any other field is filled (Dose, Frequency, Start Date, Notes, Status)
    const hasAnyOtherField = !!(med.Dose && med.Dose.trim()) || 
                             !!(med.MedicationRecurrence && med.MedicationRecurrence.trim()) ||
                             med.MedicationStartedFrom ||
                             !!(med.MedicationComments && med.MedicationComments.trim()) ||
                             !!(med.Status && med.Status.trim());
    
    // If any other field is filled but MedicationName is empty, require MedicationName
    if (hasAnyOtherField && medicationNameEmpty) {
      return true;
    }
    
    return false;
  };

  // Validate form - MedicationName is required if:
  // 1. First row is empty (initial single row)
  // 2. ANY other field (Dose, Frequency, Start Date, Notes, Status) is filled
  // If all rows are completely empty AND there are multiple rows, validation passes (no validation shown)
  const validateForm = () => {
    if (formData.length === 0) return false;
    
    // If there's only one row, check if MedicationName is required
    if (formData.length === 1) {
      const firstRow = formData[0];
      if (shouldRequireMedicationName(firstRow, 0)) {
        return false; // First row validation failed
      }
      return true;
    }
    
    // Check if all rows are completely empty (multiple rows)
    const allRowsEmpty = formData.every((med, index) => isRowEmpty(med, index));
    if (allRowsEmpty) {
      return true; // All rows empty, no validation needed
    }
    
    // Check each row - if row has any data, MedicationName becomes required
    for (let i = 0; i < formData.length; i++) {
      const med = formData[i];
      
      // Skip completely empty rows (they will be filtered out)
      if (isRowEmpty(med, i)) {
        continue;
      }
      
      // If row has any data, check if MedicationName is required
      // This ensures MedicationName is required when any other field is filled
      if (shouldRequireMedicationName(med, i)) {
        return false; // MedicationName is required but not filled
      }
    }

    return true;
  };

  // Check if there are changes to existing medications or new medications with data
  const hasChangesOrNewData = (filteredData, originalMedications) => {
    // If no original medications exist, the first row is a new medication - always show popup
    if (!originalMedications || originalMedications.length === 0) {
      return true; // First row is new medication, show popup
    }

    // Check for new rows (MedicationID === 0) with data beyond the first row
    // A row has data if it's not completely empty
    const hasNewRowsWithData = filteredData.some((med, index) => index > 0 && (med.MedicationID === 0 || !med.MedicationID) && 
      !isRowEmpty(med, index));
    
    if (hasNewRowsWithData) return true;

    // Compare existing medications with original data
    for (const med of filteredData) {
      if (med.MedicationID && med.MedicationID !== 0) {
        // Find corresponding original medication
        const original = originalMedications.find(orig => {
          const origId = orig.MedicationID || orig.medication_id || orig.id || orig.medicationId;
          return origId === med.MedicationID;
        });

        if (original) {
          // Check if any field has changed
          const origName = original.MedicationName || original.medication || '';
          const origDose = original.Dose || original.dose || '';
          const origFreq = original.MedicationRecurrence || original.frequency || '';
          const origDate = original.MedicationStartedFrom || original.startedFrom;
          const origComments = original.MedicationComments || original.comments || original.comment || original.notes || '';
          const origStatus = original.Status || original.status || '';

          // Format dates for comparison
          const formatDate = (date) => {
            if (!date) return null;
            const d = new Date(date);
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          };

          const medDate = med.MedicationStartedFrom ? formatDate(med.MedicationStartedFrom) : null;
          const origDateFormatted = origDate ? formatDate(origDate) : null;

          if (origName !== med.MedicationName ||
              origDose !== med.Dose ||
              origFreq !== med.MedicationRecurrence ||
              origDateFormatted !== medDate ||
              origComments !== med.MedicationComments ||
              origStatus !== med.Status) {
            return true; // Changes detected
          }
        }
      }
    }

    return false; // No changes or new data
  };

  const handleSave = async () => {
    setSubmitClicked(true);
    console.log(formData, "formData2")

    // If formData is empty but we had original medications, we need to delete all existing medications
    if (formData.length === 0 && medications && medications.length > 0) {
      try {
        // Delete all existing medications
        for (const med of medications) {
          // Check for different possible ID field names
          const medicationId = med.MedicationID || med.medication_id || med.id || med.medicationId;
          if (medicationId && medicationId !== 0) {
            const deletePayload = {
              UserID: userId,
              MedicationID: medicationId,
              token
            };
            await dispatch(BulkMedicationDelete(deletePayload)).unwrap();
          }
        }
        if (onSave) {
          await onSave(); // Refresh medications in parent
        }
        if (onHide) {
          onHide(); // Close modal
        }
        return;
      } catch (error) {
        return;
      }
    }

    // Validate only the first row - if it's empty, show validation errors
    if (!validateForm()) {
      //showToast('error', 'Please fill all required fields.');
      return;
    }

    // Filter out completely empty rows - only keep rows with at least one field filled
    const filteredData = formData.filter((med, index) => {
      return !isRowEmpty(med, index);
    });

    // Check if there are changes to existing medications or new medications with data
    const hasChangesOrNewRows = hasChangesOrNewData(filteredData, medications);

    try {
      // Prepare array of medication objects for API - use filteredData to exclude empty rows
      const medicationsPayload = filteredData.map(med => {
        let formattedDate = null;
        if (med.MedicationStartedFrom) {
          const d = new Date(med.MedicationStartedFrom);
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          formattedDate = `${year}-${month}-${day}`;
        }

        return {
          MedicationName: med.MedicationName,
          MedicationRecurrence: med.MedicationRecurrence,
          MedicationStartedFrom: formattedDate,
          UserID: userId,
          MedicationID: med.MedicationID || 0,
          MedicationComments: med.MedicationComments || '',
          Status: med.Status,
          Dose: med.Dose
        };
      });
      const response = await dispatch(BulkMedicationCreate({ medications: medicationsPayload, token })).unwrap();

      // The API returns true on success
      if (response === true) {
        if (onSave) {
          await onSave(); // Refresh medications in parent
        }
        
        // Only show success popup if there are changes to existing rows or new rows with data
        // If only empty rows beyond the first were filtered out, don't show popup
        if (hasChangesOrNewRows) {
          setSuccessMessage(mode === 'edit' ? 'Medications updated successfully!' : 'Medications added successfully!');
          setShowSuccessModal(true);
        }
        
        if (onHide) {
          onHide(); // Close modal
        }
        return;
      }

      // If response is not true, show error
      throw new Error('Failed to save medications. API did not return success.');
    } catch (error) {
    }
  };

  const handleBlur = (index, field, value) => {

    const trimmedValue = value.trim();
    const updated = [...formData];
    updated[index] = { ...updated[index], [field]: trimmedValue };
    setFormData(updated);
  };


  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" dialogClassName="modal-xxl" centered backdrop="static" keyboard={false} enforceFocus={false}>
        <Modal.Header closeButton>
          <Modal.Title className="modal-title-custom text-capitalize">Medications</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <Form>
            {formData.map((medication, index) => (
              <div key={index}>
                <Row className="align-items-center mb-3 g-2">
                  <Col className="custom-col-width-medication me-4">
                    <Form.Group>
                      <Form.Label>Medication</Form.Label>
                      <Form.Control
                        type="text"
                        title={medication.MedicationName || ''}
                        value={medication.MedicationName || ''}
                        onChange={(e) => handleInputChange(index, 'MedicationName', e.target.value)}
                        onBlur={(e) => handleBlur(index, 'MedicationName', e.target.value)}
                        className={`form-control ${submitClicked && shouldRequireMedicationName(medication, index) ? 'error-border' : ''}`}
                      />
                      {/* {submitClicked && !medication.MedicationName && (
                        <Form.Control.Feedback type="invalid">
                          Medication name is required
                        </Form.Control.Feedback>
                      )} */}
                    </Form.Group>
                  </Col>

                  <Col className="custom-col-width-small me-4">
                    <Form.Group>
                      <Form.Label>Dose</Form.Label>
                      <Form.Control
                        type="text"
                        title={medication.Dose || ''}
                        value={medication.Dose || ''}
                        onChange={(e) => handleInputChange(index, 'Dose', e.target.value)}
                        onBlur={(e) => handleBlur(index, 'Dose', e.target.value)}
                        className="form-control"
                      />
                      {/* {submitClicked && !medication.Dose && (
                          <Form.Control.Feedback type="invalid">
                            Dose is required
                          </Form.Control.Feedback>
                        )} */}
                    </Form.Group>
                  </Col>

                  <Col className="custom-col-width-frequency me-4">
                    <Form.Group>
                      <Form.Label>Frequency</Form.Label>
                      <Form.Control
                        type="text"
                        title={medication.MedicationRecurrence || ''}
                        value={medication.MedicationRecurrence || ''}
                        onChange={(e) => handleInputChange(index, 'MedicationRecurrence', e.target.value)}
                        onBlur={(e) => handleBlur(index, 'MedicationRecurrence', e.target.value)}
                        className="form-control"
                        maxLength={60}
                      />
                      {/* {submitClicked && !medication.MedicationRecurrence && (
                          <Form.Control.Feedback type="invalid">
                            Frequency is required
                          </Form.Control.Feedback>
                        )} */}
                    </Form.Group>
                  </Col>

                  <Col className="custom-col-width-date me-4">
                    <Form.Group>
                      <Form.Label>Start Date</Form.Label>
                      <Flatpickr
                        value={medication.MedicationStartedFrom}
                        options={{
                          dateFormat: "m/d/Y"
                        }}
                        onChange={(selectedDates, dateStr, instance) => {
                          if (selectedDates && selectedDates.length > 0) {
                            const selectedDate = new Date(selectedDates[0]);
                            selectedDate.setHours(0, 0, 0, 0);
                            handleInputChange(index, 'MedicationStartedFrom', selectedDate);
                          } else {
                            handleInputChange(index, 'MedicationStartedFrom', null);
                          }
                        }}
                        className="form-control"
                        placeholder="mm/dd/yyyy"
                      />
                    </Form.Group>
                  </Col>

                  <Col className="custom-col-width-notes me-4">
                    <Form.Group>
                      <Form.Label>Notes</Form.Label>
                      <Form.Control
                        type="text"
                        value={medication.MedicationComments || ''}
                        title={medication.MedicationComments || ''}
                        onChange={(e) => handleInputChange(index, 'MedicationComments', e.target.value)}
                        onBlur={(e) => handleBlur(index, 'MedicationComments', e.target.value)}
                        className="form-control"
                      />
                    </Form.Group>
                  </Col>

                  <Col className="custom-col-width-status me-4">
                    <Form.Group>
                      <Form.Label>Status</Form.Label>
                      <div className="input-group-validation">
                        <Form.Control
                          type="text"
                          placeholder="Active/Discontinued"
                          title={medication.Status || ''}
                          value={medication.Status || ''}
                          onChange={(e) => handleInputChange(index, 'Status', e.target.value)}
                          onBlur={(e) => handleBlur(index, 'Status', e.target.value)}// className={`form-control ${submitClicked && !medication.Status ? 'error-border' : ''}`}
                          className="form-control"
                        />
                        {submitClicked && index === 0 && !medication.Status && (
                          <span className="input-invalid-icon">
                            <i className="bi bi-exclamation-circle-fill"></i>
                          </span>
                        )}
                      </div>
                    </Form.Group>
                  </Col>

                  <Col md={1} className="d-flex align-items-end">
                    <Button
                      variant="danger"
                      className="rounded-circle p-2 me-3 mt-4"
                      style={{ width: '36px', height: '36px' }}
                      onClick={() => deleteItem(index)}
                    // disabled={mode === 'edit' && formData.length === 1}
                    >
                      <X size={18} />
                    </Button>
                    {/* Only show Add (+) button in add mode and for last row */}
                    {mode === 'add' && index === formData.length - 1 && (
                      <Button
                        variant="success"
                        className="rounded-circle p-2 mt-4"
                        style={{ width: '36px', height: '36px' }}
                        onClick={addItem}
                      >
                        <Plus size={18} />
                      </Button>
                    )}
                  </Col>
                </Row>
                {index < formData.length - 1 && <hr />}
              </div>
            ))}
          </Form>
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          {/* Show Save or Update button based on mode */}
          <Button variant="primary" onClick={handleSave} className="me-3">
            {mode === 'edit' ? 'Update' : 'Save'}
          </Button>
          <Button variant="danger" onClick={onHide} className="btn-danger">
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Custom Success Modal */}
      <CustomSuccessModal
        show={showSuccessModal}
        onHide={handleSuccessModalClose}
        message={successMessage}
      />
      <style>{`.error-border { border: 1px solid #dc3545 !important; box-shadow: none !important; } 
      .flatpickr-calendar {
                    z-index: 999999 !important;
                    position: fixed !important;
                }
      `}</style>
    </>
  );
};

export default MedicationsModal;

